import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { UserMinus, ShieldCheck, Slack, ExternalLink } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Trans, useTranslation } from 'react-i18next';

const SLACK_ADMIN_URL = 'https://winclap.slack.com/admin';

export interface BajaTarget {
  id_baja: string;
  correo: string;
  fecha_baja: string;
  decision: 'dar_de_baja' | 'mantener';
}

export function BajaDecisionDialog({
  target,
  onClose,
  onConfirm,
  pending,
}: {
  target: BajaTarget | null;
  onClose: () => void;
  onConfirm: (baja: BajaTarget) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const isBaja = target?.decision === 'dar_de_baja';

  // Slack status (Pro has no deactivation API → we only remind)
  const slackQuery = trpc.users.getSlackAccountStatus.useQuery(
    { email: target?.correo ?? '' },
    { enabled: !!target && isBaja, staleTime: 5 * 60 * 1000 }
  );

  if (!target) return null;

  return (
    <AlertDialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {isBaja ? (
              <UserMinus className="h-5 w-5 text-red-500" />
            ) : (
              <ShieldCheck className="h-5 w-5 text-green-600" />
            )}
            {isBaja ? t('users.bajadecisiondialog.dar_de_baja_la_cuenta') : t('users.bajadecisiondialog.mantener_la_cuenta')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                {isBaja ? `${t('users.bajadecisiondialog.vas_a_dar_de_baja_a')} ` : `${t('users.bajadecisiondialog.vas_a_mantener_activa_a')} `}
                <span className="font-mono font-medium text-foreground">{target.correo}</span>.
              </p>
              {isBaja ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/30">
                  <p className="font-medium text-amber-900 dark:text-amber-200">{t('users.bajadecisiondialog.que_va_a_pasar_en_orden')}</p>
                  <ol className="mt-1.5 list-decimal space-y-1 pl-5 text-amber-900/90 dark:text-amber-200/90">
                    <li><Trans i18nKey="users.bajadecisiondialog.step_suspend" components={{ b: <strong /> }} /></li>
                    <li><Trans i18nKey="users.bajadecisiondialog.step_transfer" components={{ b: <strong /> }} /></li>
                    <li><Trans i18nKey="users.bajadecisiondialog.step_verify" components={{ b: <strong /> }} /></li>
                    <li><Trans i18nKey="users.bajadecisiondialog.step_delete" components={{ b: <strong /> }} /></li>
                  </ol>
                  <p className="mt-2 text-xs text-amber-800 dark:text-amber-300">
                    {t('users.bajadecisiondialog.si_la_transferencia_falla_la_cuenta')}
                  </p>
                </div>
              ) : null}

              {isBaja && slackQuery.data?.active && (
                <div className="rounded-md border border-violet-200 bg-violet-50 p-3 dark:border-violet-800 dark:bg-violet-950/30">
                  <p className="flex items-center gap-1.5 font-medium text-violet-900 dark:text-violet-200">
                    <Slack className="h-4 w-4" /> {t('users.bajadecisiondialog.slack_no_se_desactiva_solo')}
                  </p>
                  <p className="mt-1 text-xs text-violet-900/90 dark:text-violet-200/90">
                    {t('users.bajadecisiondialog.con_el_plan_pro_hay_que')}
                  </p>
                  <a
                    href={SLACK_ADMIN_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-violet-700 hover:underline dark:text-violet-300"
                  >
                    {t('users.bajadecisiondialog.abrir_gestionar_miembros_de_slack')}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {isBaja && slackQuery.data && slackQuery.data.found && !slackQuery.data.active && (
                <p className="text-xs text-muted-foreground">{t('users.bajadecisiondialog.su_cuenta_de_slack_ya_esta')}</p>
              )}

              {!isBaja && (
                <p className="text-muted-foreground">
                  {t('users.bajadecisiondialog.no_se_toca_la_cuenta_deja')}
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            className={isBaja ? 'bg-red-600 hover:bg-red-700' : ''}
            onClick={() => onConfirm(target)}
          >
            {isBaja ? t('users.bajadecisiondialog.dar_de_baja') : 'Mantener'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
